/**
 * Task Realtime Service
 *
 * Servizio singleton per gestire la connessione realtime Supabase
 * agli aggiornamenti delle task e commenti cliente. Fornisce:
 * - Sottoscrizione efficiente con retry automatico
 * - Aggiornamento automatico dello store
 * - Custom events per TaskDrawer
 */

import { supabase } from '../supabase';
import { useProjectStore } from '../stores/projectStore';
import { eventBus } from '../events/eventBus';

// Tipi di eventi emessi dal service
export type TaskRealtimeEvent =
  | 'task-realtime-status'
  | 'task-realtime-update'
  | 'client-comment-realtime-insert';

export interface RealtimeStatusEvent {
  status: 'connected' | 'reconnecting' | 'error' | 'disconnected';
  error?: string;
  attempt?: number;
}

class TaskRealtimeService {
  private channel: any = null;
  private broadcastChannel: any = null;
  private portalBroadcastChannel: any = null;
  private commentSyncChannel: any = null;
  private isInitialized = false;
  private retryCount = 0;
  private maxRetries = 5;
  private retryTimeout: NodeJS.Timeout | null = null;
  private reconnecting = false;
  private organizationId: string | null = null;
  private currentProjectId: string | null = null;

  // Metriche
  private metrics = {
    taskUpdatesReceived: 0,
    commentInsertsReceived: 0,
    errors: 0,
    lastUpdateAt: null as Date | null,
    connectedAt: null as Date | null,
  };

  /**
   * Inizializza il servizio e sottoscrive agli aggiornamenti
   */
  async init(organizationId: string, projectId?: string) {
    console.log('[TaskRealtimeService] 🚀 init() chiamato con org:', organizationId);

    // Se già inizializzato con stessa org, skip (non resettare currentProjectId che viene gestito da setCurrentProject)
    if (this.isInitialized && this.organizationId === organizationId) {
      console.log('[TaskRealtimeService] ⏭️ Già inizializzato con stessa org, skip');
      return;
    }

    // Se organizationId è cambiato, disconnetti e riconnetti
    if (this.isInitialized && this.organizationId !== organizationId) {
      console.log('[TaskRealtimeService] 🔄 Org cambiato, disconnetto...');
      await this.disconnect();
    }

    // Verifica autenticazione
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('[TaskRealtimeService] ❌ Nessuna sessione, skip init');
      return;
    }

    this.organizationId = organizationId;
    this.currentProjectId = projectId || null;
    this.isInitialized = true;

    console.log('[TaskRealtimeService] ✅ Configurazione pronta, connetto...');
    await this.connect();
  }

  /**
   * Crea e configura il canale Supabase
   */
  private async connect() {
    console.log('[TaskRealtimeService] 🔌 connect() chiamato');
    try {
      // Cleanup canale precedente se esiste
      if (this.channel) {
        console.log('[TaskRealtimeService] 🧹 Rimuovo canale precedente');
        await supabase.removeChannel(this.channel);
        this.channel = null;
      }

      if (!this.organizationId) {
        console.warn('[TaskRealtimeService] ⚠️ No organizationId, skipping connection');
        return;
      }

      // Crea nuovo canale con ID univoco
      const channelId = `task_realtime_${Date.now()}`;
      console.log('[TaskRealtimeService] 📡 Creo canale:', channelId);
      this.channel = supabase.channel(channelId);

      if (!this.channel) {
        throw new Error('Impossibile creare canale Supabase');
      }

      // Configura sottoscrizione agli UPDATE della tabella tasks
      // Nota: tasks non ha organization_id, quindi sottoscriviamo senza filtro org
      // Il filtro per progetto è gestito nell'handler
      this.channel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'tasks'
          },
          (payload: any) => this.handleTaskUpdate(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'tasks'
          },
          (payload: any) => this.handleTaskInsert(payload)
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'tasks'
          },
          (payload: any) => this.handleTaskDelete(payload)
        )
        // task_comments (team + collaborator comments)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'task_comments',
            filter: `organization_id=eq.${this.organizationId}`
          },
          (payload: any) => this.handleTaskCommentInsert(payload)
        )
        // client_task_comments ha organization_id, quindi possiamo filtrare
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'client_task_comments',
            filter: `organization_id=eq.${this.organizationId}`
          },
          (payload: any) => this.handleClientCommentInsert(payload)
        )
        .subscribe((status: string, err?: Error) => {
          this.handleSubscriptionStatus(status, err);
        });

      // Canale broadcast per task create esternamente (es. portale cliente via supabase)
      if (this.broadcastChannel) {
        await supabase.removeChannel(this.broadcastChannel);
      }
      this.broadcastChannel = supabase.channel('task-external-inserts')
        .on('broadcast', { event: 'task-insert' }, (payload: any) => {
          console.log('[TaskRealtimeService] 📡 BROADCAST task-insert ricevuto:', payload);
          this.handleTaskInsert({ new: payload.payload });
        })
        .subscribe();

      // Canale relay per inviare aggiornamenti task al portale cliente
      // Il portale non riceve postgres_changes (auth.uid() = NULL, RLS blocca tutto)
      // Questo canale fa da ponte: CRM riceve postgres_changes → relay via broadcast → portale riceve
      if (this.portalBroadcastChannel) {
        await supabase.removeChannel(this.portalBroadcastChannel);
      }
      this.portalBroadcastChannel = supabase.channel(`task-portal-sync-${this.organizationId}`);
      await this.portalBroadcastChannel.subscribe();

      // Canale broadcast bidirezionale per sync commenti CRM ↔ portale collaboratore
      if (this.commentSyncChannel) {
        await supabase.removeChannel(this.commentSyncChannel);
      }
      this.commentSyncChannel = supabase.channel(`comment-sync-${this.organizationId}`)
        .on('broadcast', { event: 'new-comment' }, (message: any) => {
          const { task_id, comment, source } = message.payload || {};
          // Ignora commenti inviati dal CRM stesso
          if (source === 'crm') return;
          console.log('[TaskRealtimeService] 💬 BROADCAST new-comment ricevuto da collaboratore:', comment?.id);
          if (task_id && comment) {
            const store = useProjectStore.getState();
            const task = store.tasks.find(t => t.id === task_id);
            if (task) {
              // Evita duplicati
              if (task.comments?.some(c => c.id === comment.id)) return;
              store.updateTaskLocally(task_id, {
                comments: [...(task.comments || []), comment]
              });
            }

            // Emit domain event for collaborator comment (CRM-side in-app notification)
            if (source === 'collaborator') {
              this.emitCollaboratorCommentDomainEvent(comment, task_id);
            }
          }
        });
      await this.commentSyncChannel.subscribe();

    } catch (error) {
      console.error('[TaskRealtimeService] ❌ Errore connessione:', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Relay un evento task al portale cliente via broadcast
   */
  private async relayToPortal(eventType: 'INSERT' | 'UPDATE' | 'DELETE', task: any) {
    if (!this.portalBroadcastChannel) return;
    try {
      await this.portalBroadcastChannel.send({
        type: 'broadcast',
        event: 'task-sync',
        payload: { eventType, task }
      });
    } catch (err) {
      console.error('[TaskRealtimeService] Error relaying to portal:', err);
    }
  }

  /**
   * Broadcast un commento sul canale comment-sync per sincronizzare CRM ↔ portale collaboratore
   */
  async broadcastComment(taskId: string, comment: any, source: 'crm' | 'collaborator') {
    if (!this.commentSyncChannel) {
      console.warn('[TaskRealtimeService] ⚠️ commentSyncChannel non pronto, skip broadcast');
      return;
    }
    try {
      console.log('[TaskRealtimeService] 📤 Broadcasting comment:', { taskId, commentId: comment?.id, source, channel: `comment-sync-${this.organizationId}` });
      await this.commentSyncChannel.send({
        type: 'broadcast',
        event: 'new-comment',
        payload: { task_id: taskId, comment, source }
      });
      console.log('[TaskRealtimeService] ✅ Comment broadcast inviato');
    } catch (err) {
      console.error('[TaskRealtimeService] Error broadcasting comment:', err);
    }
  }

  /**
   * Handler per gli UPDATE delle task
   */
  private handleTaskUpdate(payload: any) {
    console.log('[TaskRealtimeService] 📥 TASK UPDATE ricevuto:', payload);
    const updatedTask = payload.new;

    // Relay al portale cliente (PRIMA del filtro progetto - il portale potrebbe visualizzare un progetto diverso)
    this.relayToPortal('UPDATE', updatedTask);

    // Filtra per progetto corrente: se nessun progetto è attivo o la task non appartiene al progetto corrente, skip
    if (!this.currentProjectId || updatedTask.project_id !== this.currentProjectId) {
      console.log('[TaskRealtimeService] ⏭️ Task non del progetto corrente, skip');
      return;
    }

    console.log('[TaskRealtimeService] ✅ Aggiorno task:', updatedTask.id, updatedTask.title);

    // Aggiorna metriche
    this.metrics.taskUpdatesReceived++;
    this.metrics.lastUpdateAt = new Date();

    // Emetti evento per UI
    window.dispatchEvent(new CustomEvent('task-realtime-update', {
      detail: { task: updatedTask, eventType: 'UPDATE' }
    }));

    // Aggiorna store direttamente
    useProjectStore.getState().updateTaskLocally(updatedTask.id, updatedTask);
  }

  /**
   * Handler per gli INSERT delle task
   */
  private handleTaskInsert(payload: any) {
    const newTask = payload.new;
    console.log('[TaskRealtimeService] ➕ TASK INSERT ricevuto:', newTask.id, newTask.title);

    // Relay al portale cliente
    this.relayToPortal('INSERT', newTask);

    // Filtra per progetto corrente: se nessun progetto è attivo o la task non appartiene al progetto corrente, skip
    if (!this.currentProjectId || newTask.project_id !== this.currentProjectId) {
      return;
    }

    // Aggiorna metriche
    this.metrics.taskUpdatesReceived++;
    this.metrics.lastUpdateAt = new Date();

    // Emetti evento per UI
    window.dispatchEvent(new CustomEvent('task-realtime-update', {
      detail: { task: newTask, eventType: 'INSERT' }
    }));

    // Aggiorna store - aggiungi la nuova task con defaults per campi relazionali
    useProjectStore.getState().addTaskLocally({
      ...newTask,
      comments: newTask.comments || [],
      checklist: newTask.checklist || [],
      assignee: newTask.assignee || null,
    });
  }

  /**
   * Handler per i DELETE delle task
   */
  private handleTaskDelete(payload: any) {
    const deletedTask = payload.old;

    // Relay al portale cliente
    this.relayToPortal('DELETE', deletedTask);

    // Filtra per progetto corrente: se nessun progetto è attivo o la task non appartiene al progetto corrente, skip
    if (!this.currentProjectId || deletedTask.project_id !== this.currentProjectId) {
      return;
    }

    // Aggiorna metriche
    this.metrics.taskUpdatesReceived++;
    this.metrics.lastUpdateAt = new Date();

    // Emetti evento per UI
    window.dispatchEvent(new CustomEvent('task-realtime-update', {
      detail: { task: deletedTask, eventType: 'DELETE' }
    }));

    // Aggiorna store - rimuovi la task
    useProjectStore.getState().removeTaskLocally(deletedTask.id);
  }

  /**
   * Handler per i nuovi commenti cliente
   */
  /**
   * Handler per INSERT di task_comments (commenti team/collaboratori)
   */
  private handleTaskCommentInsert(payload: any) {
    const newComment = payload.new;
    console.log('[TaskRealtimeService] 💬 TASK COMMENT INSERT ricevuto:', newComment.id, 'task:', newComment.task_id);

    this.metrics.commentInsertsReceived++;
    this.metrics.lastUpdateAt = new Date();

    const commentObj = {
      id: newComment.id,
      content: newComment.content,
      created_at: newComment.created_at,
      is_system_comment: newComment.is_system_comment || false,
      author: null as any,
      attachments: []
    };

    // Aggiungi il commento al task nello store (evita duplicati)
    const store = useProjectStore.getState();
    const task = store.tasks.find(t => t.id === newComment.task_id);
    if (task && !task.comments?.some(c => c.id === newComment.id)) {
      store.updateTaskLocally(newComment.task_id, {
        comments: [...(task.comments || []), commentObj]
      });
    }

    // Relay al portale collaboratore via broadcast
    this.broadcastComment(newComment.task_id, commentObj, 'crm');
  }

  private handleClientCommentInsert(payload: any) {
    console.log('[TaskRealtimeService] 💬 CLIENT COMMENT INSERT ricevuto:', payload);
    const newComment = payload.new;

    // Aggiorna metriche
    this.metrics.commentInsertsReceived++;
    this.metrics.lastUpdateAt = new Date();

    console.log('[TaskRealtimeService] 📤 Emetto evento client-comment-realtime-insert per task:', newComment.task_id);

    // Emetti evento per UI (TaskDrawer ascolterà)
    window.dispatchEvent(new CustomEvent('client-comment-realtime-insert', {
      detail: { comment: newComment }
    }));

    // Se il commento è dal client, emetti evento domain per notifiche
    if (newComment.author_type === 'client') {
      this.emitClientCommentDomainEvent(newComment);
    }
  }

  /**
   * Emette l'evento domain task:client_comment con i dati necessari
   */
  private async emitClientCommentDomainEvent(comment: any) {
    try {
      // Recupera dati task + progetto per costruire il payload
      const { data: task } = await supabase
        .from('tasks')
        .select(`
          id, title, assigned_to, section,
          project:projects!inner (
            id, name, project_manager_id
          )
        `)
        .eq('id', comment.task_id)
        .single();

      if (!task?.project) return;

      const project = task.project as any;

      eventBus.emitEvent({
        type: 'task:client_comment',
        payload: {
          projectId: project.id,
          taskId: task.id,
          taskTitle: task.title,
          commentId: comment.id,
          commentContent: comment.content || '',
          clientName: comment.author_name || 'Cliente',
          clientAccessId: comment.author_id || '',
          projectManagerId: project.project_manager_id || undefined,
          taskAssigneeId: task.assigned_to || undefined,
          projectName: project.name,
        }
      });
    } catch (error) {
      console.error('[TaskRealtimeService] Error emitting task:client_comment event:', error);
    }
  }

  /**
   * Emette l'evento domain task:collaborator_comment con i dati necessari
   */
  private async emitCollaboratorCommentDomainEvent(comment: any, taskId: string) {
    try {
      const { data: task } = await supabase
        .from('tasks')
        .select(`
          id, title, assigned_to, created_by,
          project:projects!inner (
            id, name, project_manager_id
          )
        `)
        .eq('id', taskId)
        .single();

      if (!task?.project) return;

      const project = task.project as any;

      // Extract collaborator name from content prefix [Collaboratore: Name]
      const nameMatch = comment.content?.match(/^\[Collaboratore: (.+?)\]/);
      const collaboratorName = nameMatch?.[1] || 'Collaboratore';

      eventBus.emitEvent({
        type: 'task:collaborator_comment',
        payload: {
          projectId: project.id,
          taskId: task.id,
          taskTitle: task.title,
          commentId: comment.id,
          commentContent: comment.content || '',
          collaboratorName,
          projectManagerId: project.project_manager_id || undefined,
          taskAssigneeId: task.assigned_to || undefined,
          taskCreatorId: task.created_by || undefined,
          projectName: project.name,
        }
      });
    } catch (error) {
      console.error('[TaskRealtimeService] Error emitting task:collaborator_comment event:', error);
    }
  }

  /**
   * Gestisce gli stati della sottoscrizione
   */
  private handleSubscriptionStatus(status: string, err?: Error) {
    console.log('[TaskRealtimeService] 📶 Status:', status, err ? `Error: ${err.message}` : '');
    switch (status) {
      case 'SUBSCRIBED':
        console.log('[TaskRealtimeService] ✅ CONNESSO! Sottoscritto a task updates');
        this.retryCount = 0;
        this.reconnecting = false;
        this.metrics.connectedAt = new Date();
        this.emitEvent('task-realtime-status', { status: 'connected' });
        break;

      case 'CHANNEL_ERROR':
        console.error('[TaskRealtimeService] ❌ Errore canale:', err?.message);
        this.metrics.errors++;
        this.handleConnectionError(err);
        break;

      case 'TIMED_OUT':
        console.error('[TaskRealtimeService] ⏰ Timeout');
        this.handleConnectionError(new Error('Timeout sottoscrizione'));
        break;

      case 'CLOSED':
        console.warn('[TaskRealtimeService] 🔌 Connection closed');
        if (this.isInitialized && !this.reconnecting) {
          // Riconnetti automaticamente se chiuso inaspettatamente
          this.scheduleReconnect();
        }
        break;
    }
  }

  /**
   * Gestisce errori di connessione con retry exponential backoff
   */
  private handleConnectionError(error: any) {
    if (this.retryCount >= this.maxRetries) {
      console.error('[TaskRealtimeService] 💀 Max tentativi raggiunto');
      this.emitEvent('task-realtime-status', {
        status: 'error',
        error: 'Impossibile connettersi'
      });
      return;
    }

    this.scheduleReconnect();
  }

  /**
   * Pianifica riconnessione con exponential backoff
   */
  private scheduleReconnect() {
    if (this.reconnecting || this.retryTimeout) {
      return; // Già in fase di riconnessione
    }

    this.reconnecting = true;
    this.retryCount++;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 16000);

    this.emitEvent('task-realtime-status', {
      status: 'reconnecting',
      attempt: this.retryCount
    });

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.connect();
    }, delay);
  }

  /**
   * Emette un evento custom
   */
  private emitEvent(eventName: TaskRealtimeEvent, detail: any) {
    try {
      const event = new CustomEvent(eventName, { detail });
      window.dispatchEvent(event);
    } catch (error) {
      console.error(`[TaskRealtimeService] Errore emissione evento ${eventName}:`, error);
    }
  }

  /**
   * Cambia il progetto corrente (per filtrare solo task di quel progetto)
   */
  setCurrentProject(projectId: string | null) {
    this.currentProjectId = projectId;
  }

  /**
   * Disconnette il servizio
   */
  async disconnect() {
    this.isInitialized = false;
    this.reconnecting = false;
    this.organizationId = null;
    this.currentProjectId = null;

    // Cancella retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Rimuovi canali
    if (this.channel) {
      await supabase.removeChannel(this.channel);
      this.channel = null;
    }
    if (this.broadcastChannel) {
      await supabase.removeChannel(this.broadcastChannel);
      this.broadcastChannel = null;
    }
    if (this.portalBroadcastChannel) {
      await supabase.removeChannel(this.portalBroadcastChannel);
      this.portalBroadcastChannel = null;
    }
    if (this.commentSyncChannel) {
      await supabase.removeChannel(this.commentSyncChannel);
      this.commentSyncChannel = null;
    }

    this.emitEvent('task-realtime-status', { status: 'disconnected' });
  }

  /**
   * Ottieni metriche del servizio
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Forza riconnessione
   */
  async reconnect() {
    const orgId = this.organizationId;
    const projectId = this.currentProjectId;
    await this.disconnect();
    if (orgId) {
      await this.init(orgId, projectId || undefined);
    }
  }
}

// Export singleton instance
export const taskRealtimeService = new TaskRealtimeService();
